<script setup lang="ts">
import { ref, computed } from 'vue';
import { getWishfulTimes } from '@/javascripts/utils/inquiry_form_options';
import SlideIndicator from './slide_indicator.vue';
import TermOfServiceModal from '@/javascripts/common/kiyaku.vue'
import PrivacyPolicyModal from '@/javascripts/common/privacy_policy_modal.vue'
import LoadingAnimation from '@/javascripts/common/loading_animation.vue'
import type { InquiryValidation } from '@/javascripts/composables/inquiry/validation';
import Inquiry from '@/javascripts/utils/inquiry';

const emit = defineEmits(['next', 'back']);

const props = defineProps<{
  inquiry: Inquiry,
  inquiryValidation: InquiryValidation,
  currentSlideIndex: number,
}>();

const wishfulTimes = getWishfulTimes();

/** 利用規約モーダルコンポーネント */
const termOfServiceModal = ref<InstanceType<typeof TermOfServiceModal> | null>();
/** 個人情報保護方針モーダルコンポーネント */
const privacyPolicyModal = ref<InstanceType<typeof PrivacyPolicyModal> | null>();
/** ローディングアニメーションコンポーネント */
const loadingAnimation = ref<InstanceType<typeof LoadingAnimation> | null>();

const invalid = computed(() => 
  props.inquiryValidation.email.$invalid ||
  props.inquiryValidation.wishfulTimeCd.$invalid
);

/** 利用規約モーダルの表示 */
const openTermOfServiceModal = () => {
  termOfServiceModal.value?.openModal();
}

/** 個人情報保護方針モーダルの表示 */
const openPrivacyPolicyModal = () => {
  privacyPolicyModal.value?.openModal();
}

/** 問い合わせデータを送信 */
const post = () => {
  loadingAnimation.value?.show();
  props.inquiry.postShindanInquiry();
}
</script>

<template>
  <div class="inner_content inner_step">
    <LoadingAnimation ref="loadingAnimation" />
    <div class="main_content step_content">
      <TermOfServiceModal ref="termOfServiceModal" />
      <PrivacyPolicyModal ref="privacyPolicyModal" />
      <SlideIndicator :current-slide-index="currentSlideIndex" />
      <div class="box_form">
        <p class="txt_step">
          年収・時給診断の結果を見るには、薬キャリエージェントの転職支援サービスにご登録いただく必要があります。ご希望の条件やプロフィールをもとに最適な求人をご紹介いたします。ぜひ年収・時給診断とともに転職支援サービスにご登録ください。
        </p>
        <table>
          <tr>
            <th>
              メールアドレス
            </th>
            <td class="input_l">
              <input
                v-model="inquiry.email"
                required
                autocapitalize="off"
                name="email"
                placeholder="abc@m3.com"
                type="email"
              >
            </td>
          </tr>
          <tr>
            <th class="view_pc" />
            <td class="form_error">
              <p
                v-for="(error, index) in inquiryValidation.email.$errors"
                :key="index"
                class="form_errormessage"
              >
                {{ error.$message }}
              </p>
            </td>
          </tr>
          <tr class="box_input box_input_btn">
            <th class="top_tit">
              希望就業時期
            </th>
            <td class="inner_input_btn02">
              <div
                v-for="wishfulTime in wishfulTimes"
                :key="wishfulTime.cd"  
                class="step_btn"
              >
                <label
                  class="shindan_radio_label shindan_label_wishfultime"
                  :for="'wishful_time_cd_'+ wishfulTime.cd"
                >
                  <input
                    :id="'wishful_time_cd_' + wishfulTime.cd"
                    v-model="inquiry.wishfulTimeCd"
                    class="shindan_radio_input"
                    type="radio"
                    name="wishful_time_cd"
                    :value="wishfulTime.cd"
                  >
                  <p>{{ wishfulTime.text }}</p>
                </label>
              </div>
            </td>
          </tr>
          <tr>
            <th class="view_pc" />
            <td class="form_error">
              <p
                v-for="(error, index) in inquiryValidation.wishfulTimeCd.$errors"
                :key="index"
                class="form_errormessage"
              >
                {{ error.$message }}
              </p>
            </td>
          </tr>
        </table>
      </div>
      <div class="box_btn_nextback box_btn_nextback_fin">
        <div class="btn_back">
          <button @click.once="emit('back')">
            <span>戻る</span>
          </button>
        </div>
        <div class="btn_next btn_next_fin">
          <div class="txt_btn_next">
            <span
              class="modal_link"
              @click="openTermOfServiceModal()"
            >利用規約</span>・
            <span
              class="modal_link"
              @click="openPrivacyPolicyModal()"
            >個人情報保護方針</span>に同意し、
          </div>
          <button
            id="submit_btn"
            :disabled="invalid"
            class="shindan_submit"
            @click.once="post()"
          >
            <p>転職支援サービスに登録し<br>診断結果を見る</p>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
